import { AnimatePresence, motion } from 'framer-motion';
import {
    ArrowRight,
    CheckCircle,
    Eye,
    Key,
    Send,
    Shield,
    Smartphone,
    Users,
    Wallet,
    Zap
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            id: 1,
            title: "DocumintID Creation",
            subtitle: "The Foundation of Trust",
            description: "Both issuer and holder create their unique DocumintIDs - their digital identity anchors in the decentralized world. Think of it as your permanent, tamper-proof digital passport that lives on the blockchain.",
            icon: Key,
            color: "blue",
            illustration: "🏗️",
            details: [
                "Issuer creates institutional DocumintID",
                "Holder creates personal DocumintID", 
                "Both IDs are cryptographically secured",
                "Permanent digital identity established"
            ]
        },
        {
            id: 2,
            title: "Credential Issuance",
            subtitle: "Digital Certification Magic",
            description: "The issuer uses their DocumintID to create and digitally sign a verifiable credential for the holder. This isn't just a digital copy - it's a cryptographically secured, tamper-evident credential that's more secure than any physical document.",
            icon: Send,
            color: "green",
            illustration: "✨",
            details: [
                "Issuer digitally signs the credential",
                "Holder's DID is embedded as recipient",
                "Cryptographic proof of authenticity",
                "Immutable record on blockchain"
            ]
        },
        {
            id: 3,
            title: "Wallet Reception",
            subtitle: "Your Digital Inbox",
            description: "The credential appears in the holder's Documint Wallet as a pending request. Like receiving a registered letter, the holder can review, verify the issuer's authenticity, and choose to accept this digital treasure into their personal credential vault.",
            icon: Smartphone,
            color: "purple",
            illustration: "📱",
            details: [
                "Credential request notification",
                "Issuer identity verification",
                "Holder reviews credential details",
                "Secure acceptance process"
            ]
        },
        {
            id: 4,
            title: "Secure Storage",
            subtitle: "Your Personal Digital Vault",
            description: "Once accepted, the credential is encrypted and stored securely in the holder's wallet. It's like having a bank vault in your pocket - only you have the keys, and the contents are protected by military-grade encryption.",
            icon: Wallet,
            color: "orange",
            illustration: "🔐",
            details: [
                "End-to-end encryption",
                "Local secure storage",
                "Backup and recovery options",
                "Complete holder control"
            ]
        },
        {
            id: 5,
            title: "Verification Request",
            subtitle: "The Moment of Truth",
            description: "When a verifier needs proof, they request specific credentials. The holder reviews exactly what's being asked for and decides what to share. It's like showing your ID, but you control exactly which information is visible.",
            icon: Eye,
            color: "indigo",
            illustration: "👁️",
            details: [
                "Verifier requests specific data",
                "Holder reviews request details",
                "Selective information disclosure",
                "Privacy-preserving sharing"
            ]
        },
        {
            id: 6,
            title: "Instant Verification",
            subtitle: "The WoW Moment",
            description: "The magic happens! The verifier instantly confirms the credential's authenticity without contacting the issuer. No phone calls, no waiting, no fraud - just instant, cryptographic proof that creates that jaw-dropping 'How did they do that?' moment.",
            icon: Zap,
            color: "emerald",
            illustration: "⚡",
            details: [
                "Instant cryptographic verification",
                "No issuer contact needed",
                "Tamper detection technology",
                "Real-time authenticity proof"
            ]
        }
    ];

    const getColorClasses = (color) => {
        const colors = {
            blue: {
                bg: "from-blue-500/20 to-cyan-500/20",
                border: "border-blue-500/30",
                icon: "text-blue-400",
                accent: "text-blue-300"
            },
            green: {
                bg: "from-green-500/20 to-emerald-500/20",
                border: "border-green-500/30",
                icon: "text-green-400",
                accent: "text-green-300"
            },
            purple: {
                bg: "from-purple-500/20 to-pink-500/20",
                border: "border-purple-500/30",
                icon: "text-purple-400",
                accent: "text-purple-300"
            },
            orange: {
                bg: "from-orange-500/20 to-red-500/20",
                border: "border-orange-500/30",
                icon: "text-orange-400",
                accent: "text-orange-300"
            },
            indigo: {
                bg: "from-indigo-500/20 to-blue-500/20",
                border: "border-indigo-500/30",
                icon: "text-indigo-400",
                accent: "text-indigo-300"
            },
            emerald: {
                bg: "from-emerald-500/20 to-green-500/20",
                border: "border-emerald-500/30",
                icon: "text-emerald-400",
                accent: "text-emerald-300"
            }
        };
        return colors[color] || colors.blue;
    };

    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-neon-500/20 to-primary-500/20 rounded-full mb-6 border border-neon-500/30">
                            <span className="text-neon-300 font-semibold text-sm">✨ PREPARE TO BE AMAZED</span>
                        </div>
                        
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            How the 
                            <span className="bg-gradient-to-r from-neon-300 via-primary-400 to-neon-300 bg-clip-text text-transparent"> Magic </span>
                            Happens
                        </h1>
                        
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            Witness the revolutionary 6-step journey that transforms traditional credentials into 
                            <strong className="text-white"> secure, instant, and fraud-proof digital assets</strong>. 
                            This isn&apos;t just technology - it&apos;s digital alchemy that will leave you speechless.
                        </p>
                    </motion.div>

                    {/* Interactive Step Navigation */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16"
                    >
                        <div className="flex justify-center gap-2 overflow-x-auto">
                            {steps.map((step, index) => (
                                <button
                                    key={step.id}
                                    onClick={() => setActiveStep(index)}
                                    className={`relative group px-4 py-2 rounded-lg transition-all duration-300 flex-shrink-0 ${
                                        activeStep === index
                                            ? `bg-gradient-to-r ${getColorClasses(step.color).bg} ${getColorClasses(step.color).border} border`
                                            : 'bg-surface-200/20 border border-white/10 hover:bg-surface-200/40'
                                    }`}
                                >
                                    <div className="flex items-center space-x-2">
                                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                                            activeStep === index ? getColorClasses(step.color).bg : 'bg-surface-200/30'
                                        }`}>
                                            <step.icon className={`w-3 h-3 ${
                                                activeStep === index ? getColorClasses(step.color).icon : 'text-gray-400'
                                            }`} />
                                        </div>
                                        <div className="text-left">
                                            <div className={`font-semibold text-xs ${
                                                activeStep === index ? 'text-white' : 'text-gray-300'
                                            }`}>
                                                Step {step.id}
                                            </div>
                                            <div className={`text-xs ${
                                                activeStep === index ? getColorClasses(step.color).accent : 'text-gray-400'
                                            }`}>
                                                {step.title}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Main Content Section */}
            <section className="py-20 bg-surface-200">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Side - Content */}
                        <div className="relative min-h-[600px]">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`step-content-${activeStep}`}
                                    initial={{ opacity: 0, x: -50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{ duration: 0.6 }}
                                    className="absolute inset-0"
                                >
                                <div className="mb-8">
                                    <div className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${getColorClasses(steps[activeStep].color).bg} rounded-full mb-6 border ${getColorClasses(steps[activeStep].color).border}`}>
                                        <span className="text-2xl mr-2">{steps[activeStep].illustration}</span>
                                        <span className={`font-semibold text-sm ${getColorClasses(steps[activeStep].color).accent}`}>
                                            STEP {steps[activeStep].id}
                                        </span>
                                    </div>
                                    
                                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                                        {steps[activeStep].title}
                                    </h2>
                                    
                                    <h3 className={`text-xl font-semibold mb-6 ${getColorClasses(steps[activeStep].color).accent}`}>
                                        {steps[activeStep].subtitle}
                                    </h3>
                                    
                                    <p className="text-lg text-gray-300 leading-relaxed mb-8">
                                        {steps[activeStep].description}
                                    </p>
                                </div>

                                {/* Step Details */}
                                <div className="space-y-4 mb-8">
                                    {steps[activeStep].details.map((detail, index) => (
                                        <div
                                            key={`detail-${activeStep}-${index}`}
                                            className="flex items-start space-x-3"
                                        >
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${getColorClasses(steps[activeStep].color).bg}`}>
                                                <CheckCircle className={`w-4 h-4 ${getColorClasses(steps[activeStep].color).icon}`} />
                                            </div>
                                            <span className="text-gray-300">{detail}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Navigation Buttons */}
                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                                        disabled={activeStep === 0}
                                        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous Step
                                    </button>
                                    <button
                                        onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                                        disabled={activeStep === steps.length - 1}
                                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                    >
                                        {activeStep === steps.length - 1 ? 'Experience Complete! 🎉' : 'Next Step'}
                                        {activeStep !== steps.length - 1 && <ArrowRight size={16} className="ml-2" />}
                                    </button>
                                </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Right Side - Interactive Illustration */}
                        <motion.div
                            key={activeStep}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="relative">
                                {/* Background Glow Effects */}
                                <div className={`absolute inset-0 bg-gradient-to-r ${getColorClasses(steps[activeStep].color).bg} rounded-3xl blur-3xl opacity-60`}></div>
                                <div className="absolute -inset-4 bg-gradient-to-r from-neon-400/10 via-primary-500/10 to-neon-400/10 rounded-3xl blur-2xl"></div>
                                
                                <div className="relative glass rounded-2xl p-8 border border-white/20">
                                    {/* Step Illustration */}
                                    <div className="text-center mb-8">
                                        <div className={`w-24 h-24 mx-auto rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-r ${getColorClasses(steps[activeStep].color).bg} border ${getColorClasses(steps[activeStep].color).border}`}>
                                            <span className="text-4xl">{steps[activeStep].illustration}</span>
                                        </div>
                                        <h4 className="text-xl font-bold text-white mb-2">
                                            {steps[activeStep].title}
                                        </h4>
                                        <p className={`text-sm ${getColorClasses(steps[activeStep].color).accent}`}>
                                            {steps[activeStep].subtitle}
                                        </p>
                                    </div>

                                    {/* Interactive Elements based on step */}
                                    {activeStep === 0 && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border border-blue-500/30">
                                                    <div className="text-center">
                                                        <div className="w-12 h-12 bg-blue-500/30 rounded-lg mx-auto mb-2 flex items-center justify-center">
                                                            <Users className="w-6 h-6 text-blue-400" />
                                                        </div>
                                                        <div className="text-sm font-medium text-white">Issuer</div>
                                                        <div className="text-xs text-blue-300">Creates DID</div>
                                                    </div>
                                                </div>
                                                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-500/30">
                                                    <div className="text-center">
                                                        <div className="w-12 h-12 bg-green-500/30 rounded-lg mx-auto mb-2 flex items-center justify-center">
                                                            <Users className="w-6 h-6 text-green-400" />
                                                        </div>
                                                        <div className="text-sm font-medium text-white">Holder</div>
                                                        <div className="text-xs text-green-300">Creates DID</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeStep === 1 && (
                                        <div className="space-y-4">
                                            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-500/30">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <Send className="w-6 h-6 text-green-400" />
                                                        <div>
                                                            <div className="text-sm font-medium text-white">Credential Issued</div>
                                                            <div className="text-xs text-green-300">Digitally Signed</div>
                                                        </div>
                                                    </div>
                                                    <div className="text-green-400">✓</div>
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl mb-2">🔐</div>
                                                <div className="text-xs text-gray-400">Cryptographically Secured</div>
                                            </div>
                                        </div>
                                    )}

                                    {activeStep === 2 && (
                                        <div className="space-y-4">
                                            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
                                                <div className="text-center">
                                                    <Smartphone className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                                                    <div className="text-sm font-medium text-white">Documint Wallet</div>
                                                    <div className="text-xs text-purple-300">Incoming Request</div>
                                                </div>
                                            </div>
                                            <div className="flex justify-center space-x-4">
                                                <button className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-xs border border-green-500/30">
                                                    Accept
                                                </button>
                                                <button className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-xs border border-red-500/30">
                                                    Decline
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {activeStep === 3 && (
                                        <div className="space-y-4">
                                            <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-4 border border-orange-500/30">
                                                <div className="text-center">
                                                    <Wallet className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                                                    <div className="text-sm font-medium text-white">Secure Storage</div>
                                                    <div className="text-xs text-orange-300">Encrypted & Protected</div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                <div className="h-8 bg-blue-500/20 rounded border border-blue-500/30"></div>
                                                <div className="h-8 bg-green-500/20 rounded border border-green-500/30"></div>
                                                <div className="h-8 bg-purple-500/20 rounded border border-purple-500/30"></div>
                                            </div>
                                            <div className="text-center text-xs text-gray-400">Your Credentials</div>
                                        </div>
                                    )}

                                    {activeStep === 4 && (
                                        <div className="space-y-4">
                                            <div className="bg-gradient-to-r from-indigo-500/20 to-blue-500/20 rounded-xl p-4 border border-indigo-500/30">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <Eye className="w-6 h-6 text-indigo-400" />
                                                        <div>
                                                            <div className="text-sm font-medium text-white">Verification Request</div>
                                                            <div className="text-xs text-indigo-300">Review & Approve</div>
                                                        </div>
                                                    </div>
                                                    <div className="text-indigo-400">👁️</div>
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-xs text-gray-400 mb-2">Share only what&apos;s needed</div>
                                                <div className="flex justify-center space-x-2">
                                                    <div className="w-4 h-4 bg-green-500/30 rounded"></div>
                                                    <div className="w-4 h-4 bg-gray-500/30 rounded"></div>
                                                    <div className="w-4 h-4 bg-gray-500/30 rounded"></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeStep === 5 && (
                                        <div className="space-y-4">
                                            <div className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-xl p-4 border border-emerald-500/30">
                                                <div className="text-center">
                                                    <Zap className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                                                    <div className="text-sm font-medium text-white">Instant Verification</div>
                                                    <div className="text-xs text-emerald-300">The WoW Moment!</div>
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-3xl mb-2">⚡</div>
                                                <div className="text-xs text-emerald-400 font-medium">VERIFIED INSTANTLY</div>
                                                <div className="text-xs text-gray-400">No waiting • No fraud • Pure magic</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Progress Indicator */}
                                    <div className="mt-8 pt-4 border-t border-white/20">
                                        <div className="flex justify-center items-center space-x-2">
                                            {steps.map((_, index) => (
                                                <div
                                                    key={index}
                                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                        index === activeStep
                                                            ? `${getColorClasses(steps[activeStep].color).bg.includes('blue') ? 'bg-blue-400' : 
                                                               getColorClasses(steps[activeStep].color).bg.includes('green') ? 'bg-green-400' :
                                                               getColorClasses(steps[activeStep].color).bg.includes('purple') ? 'bg-purple-400' :
                                                               getColorClasses(steps[activeStep].color).bg.includes('orange') ? 'bg-orange-400' :
                                                               getColorClasses(steps[activeStep].color).bg.includes('indigo') ? 'bg-indigo-400' :
                                                               'bg-emerald-400'}`
                                                            : 'bg-gray-600'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Experience the Magic?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Join thousands who have already witnessed this revolutionary transformation of digital identity.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/documint-wallet" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Get Your Free Wallet
                                <ArrowRight size={20} className="ml-2" />
                            </Link>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                See Live Demo
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default HowItWorks;